import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Add auth token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('webmail_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 → redirect to login (but not on public pages or profile check)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const publicPaths = ['/', '/login', '/signup', '/equity'];
    const isProfileCheck = err.config?.url?.includes('/auth/me');
    const isPublicPage = publicPaths.includes(window.location.pathname);
    if (err.response?.status === 401 && !isProfileCheck && !isPublicPage) {
      localStorage.removeItem('webmail_token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const signup = ({ email, password, displayName, recoveryEmail, domain }) => api.post('/auth/signup', { email, password, displayName, recoveryEmail, domain }).then((r) => r.data);
export const login = (email, password) => api.post('/auth/login', { email, password }).then((r) => r.data);
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email }).then((r) => r.data);
export const resetPassword = ({ email, code, newPassword }) => api.post('/auth/reset-password', { email, code, newPassword }).then((r) => r.data);
export const setPassword = (password) => api.post('/auth/set-password', { password }).then((r) => r.data);

// MFA
export const getMfaStatus = () => api.get('/auth/mfa/status').then((r) => r.data);
export const setupTOTP = () => api.post('/auth/mfa/totp/setup').then((r) => r.data);
export const verifyTOTP = (token) => api.post('/auth/mfa/totp/verify', { token }).then((r) => r.data);
export const setupSMSMfa = (phone) => api.post('/auth/mfa/sms/setup', { phone }).then((r) => r.data);
export const verifySMSMfa = (code) => api.post('/auth/mfa/sms/verify', { code }).then((r) => r.data);
export const disableMFA = (password) => api.post('/auth/mfa/disable', { password }).then((r) => r.data);
export const validateMFA = (data) => api.post('/auth/mfa/validate', data).then((r) => r.data);
export const logout = () => api.post('/auth/logout').then((r) => r.data);
export const getProfile = () => api.get('/auth/me').then((r) => r.data);
export const updateProfile = (data) => api.patch('/auth/me', data).then((r) => r.data);
export const verifyRecoveryEmail = (code) => api.post('/auth/verify-recovery', { code }).then((r) => r.data);
export const resendVerification = () => api.post('/auth/resend-verification').then((r) => r.data);

// Domains (pre-auth, for business signup)
export const checkDomain = (domain) => api.post('/auth/check-domain', { domain }).then((r) => r.data);
export const purchaseDomain = (domain) => api.post('/v1/domains/purchase', { domain }).then((r) => r.data);
export const setupDomain = (domain) => api.post('/v1/domains/setup', { domain }).then((r) => r.data);
export const checkUsername = (username, domain) => api.get(`/auth/check-username/${username}`, { params: domain ? { domain } : {} }).then((r) => r.data);
export const getCounts = () => api.get('/emails/stats/unread').then((r) => r.data);
export const getEmails = (params) => api.get('/emails', { params }).then((r) => r.data);
export const getEmail = (id) => api.get(`/emails/${id}`).then((r) => r.data);
export const updateEmail = (id, data) => api.patch(`/emails/${id}`, data).then((r) => r.data);
export const batchUpdate = (ids, action) => api.post('/emails/batch', { ids, action }).then((r) => r.data);
export const deleteEmail = (id) => api.delete(`/emails/${id}`).then((r) => r.data);
export const sendEmail = (data) => api.post('/send', data).then((r) => r.data);

export async function sendEmailWithAttachments({ to, cc, bcc, subject, text, html, inReplyTo, threadId, files }) {
  // Convert files to base64 for JSON transmission (no multer needed on backend)
  const attachments = [];
  for (const file of (files || [])) {
    const base64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(file);
    });
    attachments.push({ filename: file.name, content: base64, contentType: file.type });
  }

  // Parse recipients into arrays
  const toList = typeof to === 'string' ? to.split(',').map(e => e.trim()).filter(Boolean) : (to || []);
  const ccList = cc ? (typeof cc === 'string' ? cc.split(',').map(e => e.trim()).filter(Boolean) : cc) : [];
  const bccList = bcc ? (typeof bcc === 'string' ? bcc.split(',').map(e => e.trim()).filter(Boolean) : bcc) : [];

  return api.post('/send', {
    to: toList,
    cc: ccList,
    bcc: bccList,
    subject,
    text,
    html,
    inReplyTo,
    threadId,
    attachments,
  }, { timeout: 120000 }).then((r) => r.data);
}

// Drafts
export const saveDraft = (data) => api.post('/send/draft', data).then((r) => r.data);
export const updateDraft = (id, data) => api.post('/send/draft', { ...data, draftId: id }).then((r) => r.data);
export const deleteDraft = (id) => api.delete(`/emails/${id}`).then((r) => r.data);

// Undo send
export const queueSend = (data) => api.post('/send/queue', data).then((r) => r.data);
export const cancelSend = (id) => api.post(`/send/cancel/${id}`).then((r) => r.data);

// Threads
export const getThread = (threadId) => api.get(`/emails/thread/${encodeURIComponent(threadId)}`).then((r) => r.data);

// Signatures
export const getSignatures = () => api.get('/signatures').then((r) => r.data);
export const createSignature = (data) => api.post('/signatures', data).then((r) => r.data);
export const updateSignature = (id, data) => api.patch(`/signatures/${id}`, data).then((r) => r.data);
export const deleteSignature = (id) => api.delete(`/signatures/${id}`).then((r) => r.data);

// Vacation responder
export const getVacation = () => api.get('/vacation').then((r) => r.data);
export const setVacation = (data) => api.put('/vacation', data).then((r) => r.data);
export const deleteVacation = () => api.delete('/vacation').then((r) => r.data);

// Contact auto-complete
export const suggestContacts = (q) => {
  // q may contain "&current=..." appended by ChipInput
  const [query, ...rest] = q.split('&current=');
  const current = rest.join('');
  return api.get('/contacts/suggest', { params: { q: query, current } }).then((r) => r.data);
};

// Push notifications
export const getVapidKey = () => api.get('/push/vapid-key').then((r) => r.data);
export const subscribePush = (data) => api.post('/push/subscribe', data).then((r) => r.data);
export const unsubscribePush = (endpoint) => api.delete('/push/unsubscribe', { data: { endpoint } }).then((r) => r.data);

// Labels
export const getLabels = () => api.get('/labels').then((r) => r.data);
export const createLabel = (data) => api.post('/labels', data).then((r) => r.data);
export const updateLabel = (id, data) => api.patch(`/labels/${id}`, data).then((r) => r.data);
export const deleteLabel = (id) => api.delete(`/labels/${id}`).then((r) => r.data);
export const assignLabels = (emailId, labelIds) => api.post(`/emails/${emailId}/labels`, { label_ids: labelIds }).then((r) => r.data);
export const removeLabel = (emailId, labelId) => api.delete(`/emails/${emailId}/labels/${labelId}`).then((r) => r.data);

// Filters
export const getFilters = () => api.get('/filters').then((r) => r.data);
export const createFilter = (data) => api.post('/filters', data).then((r) => r.data);
export const updateFilter = (id, data) => api.patch(`/filters/${id}`, data).then((r) => r.data);
export const deleteFilter = (id) => api.delete(`/filters/${id}`).then((r) => r.data);

// Spam
export const reportSpam = (id) => api.post(`/emails/${id}/spam`).then((r) => r.data);
export const reportNotSpam = (id) => api.post(`/emails/${id}/not-spam`).then((r) => r.data);

// External Accounts (IMAP)
export const getExternalAccounts = () => api.get('/external-accounts').then((r) => r.data);
export const addExternalAccount = (data) => api.post('/external-accounts', data).then((r) => r.data);
export const deleteExternalAccount = (id) => api.delete(`/external-accounts/${id}`).then((r) => r.data);
export const syncExternalAccount = (id) => api.post(`/external-accounts/${id}/sync`).then((r) => r.data);
export const getSyncStatus = (id) => api.get(`/external-accounts/${id}/sync/status`).then((r) => r.data);

// Todos
export const getTodos = () => api.get('/todos').then((r) => r.data);
export const createTodo = (data) => api.post('/todos', data).then((r) => r.data);
export const updateTodo = (id, data) => api.patch(`/todos/${id}`, data).then((r) => r.data);
export const deleteTodo = (id) => api.delete(`/todos/${id}`).then((r) => r.data);

// Billing
export const getBillingItems = () => api.get('/billing/items').then((r) => r.data);
export const startBillingScan = () => api.post('/billing/scan/start').then((r) => r.data);
export const stopBillingScan = () => api.post('/billing/scan/stop').then((r) => r.data);
export const getBillingScanStatus = () => api.get('/billing/scan/status').then((r) => r.data);
export const toggleBillingExclude = (id, isExcluded) => api.patch(`/billing/items/${id}/exclude`, { is_excluded: isExcluded }).then((r) => r.data);
export const excludePayee = (payee, isExcluded) => api.patch('/billing/payee/exclude', { payee, is_excluded: isExcluded }).then((r) => r.data);

// Reports
export const getBriefing = (period = 'weekly', account) => api.get('/reports/briefing', { params: { period, account } }).then((r) => r.data);
export const getLlmUsage = () => api.get('/reports/llm-usage').then((r) => r.data);

// AI Classification
export const classifyEmailAi = (id) => api.post(`/emails/${id}/classify`).then((r) => r.data);
export const classifyBatchAi = (ids) => api.post('/emails/classify-batch', { ids }).then((r) => r.data);

// Email Body (for IMAP emails)
export const getEmailBody = (id) => api.get(`/emails/${id}/body`).then((r) => r.data);

// Admin
export const getAdminMe = () => api.get('/admin/me').then((r) => r.data);
export const getOrg = (orgId) => api.get(`/admin/orgs/${orgId}`).then((r) => r.data);
export const updateOrg = (orgId, data) => api.patch(`/admin/orgs/${orgId}`, data).then((r) => r.data);
export const addOrgMember = (orgId, data) => api.post(`/admin/orgs/${orgId}/members`, data).then((r) => r.data);
export const updateOrgMember = (orgId, memberId, data) => api.patch(`/admin/orgs/${orgId}/members/${memberId}`, data).then((r) => r.data);
export const removeOrgMember = (orgId, memberId) => api.delete(`/admin/orgs/${orgId}/members/${memberId}`).then((r) => r.data);
export const connectDomain = (orgId, domain) => api.post(`/admin/orgs/${orgId}/domain`, { domain }).then((r) => r.data);
export const getPlatformOrgs = () => api.get('/admin/platform/orgs').then((r) => r.data);
export const getPlatformUsers = () => api.get('/admin/platform/users').then((r) => r.data);
export const createPlatformOrg = (data) => api.post('/admin/platform/orgs', data).then((r) => r.data);
export const setSuperAdmin = (userId, isSuperAdmin) =>
  api.post(`/admin/platform/users/${userId}/super-admin`, { is_super_admin: isSuperAdmin }).then((r) => r.data);
export const getOrgAddresses = (orgId) => api.get(`/admin/orgs/${orgId}/addresses`).then((r) => r.data);

// App mailboxes — API-created account addresses, visible to account members (SSO)
export const getAppMailboxAccounts = () => api.get('/app-mailboxes/accounts').then((r) => r.data);
export const getAppMailboxMessages = (accountId, { emailAddressId, q, page = 1, limit = 50 } = {}) =>
  api.get(`/app-mailboxes/accounts/${accountId}/messages`, {
    params: { email_address_id: emailAddressId || undefined, q: q || undefined, page, limit },
  }).then((r) => r.data);
export const getAppMailboxMessage = (accountId, messageId) =>
  api.get(`/app-mailboxes/accounts/${accountId}/messages/${messageId}`).then((r) => r.data);
export const sendFromAppMailbox = (accountId, body) =>
  api.post(`/app-mailboxes/accounts/${accountId}/send`, body).then((r) => r.data);
